import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import dayjs from 'dayjs'
import specializedReportService from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import useLatestRun from '@hooks/useLatestRun'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { dateRangeField } from '@components/reports/specializedFilterFields'
import AutoReportGrid from '@components/reports/AutoReportGrid'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'

const labelFromKey = (key) =>
  String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v)
const isRowArray = (v) => Array.isArray(v) && v.length > 0 && v.every(isPlainObject)

/**
 * Payload shape (what we could determine statically): cb-api's
 * POST /reports/daily (cb-api/src/domains/reports/routes.js) is a thin proxy —
 * it forwards the body to CB MVC /web/DailyReport/GetDailyReport and returns
 * `{ data: camelize(resp.data) }` with NO shaping beyond deep key-camelization.
 * The legacy DailyReportController itself is not in this repo (legacy-parity.md
 * only lists its Index/PrintDailyReport actions), so the exact JSON contract of
 * GetDailyReport is not statically determinable here. The legacy Daily/Status
 * report is a day's activity digest, which plausibly arrives as several blocks
 * (e.g. time / expense / payment sections) rather than one flat array — so the
 * unwrap below is defensive:
 *   - a bare array, or an object with a single array container key
 *     (rows/data/report/results/items) → one grid, as before
 *   - an object whose values are multiple rows[] arrays ({sectionName: rows[]}
 *     style, possibly nested one level under a container key) → one stacked
 *     AutoReportGrid per section with a humanized section heading
 */
const CONTAINER_KEYS = ['rows', 'data', 'report', 'results', 'items']

const toSections = (data, depth = 0) => {
  if (Array.isArray(data)) return [{ key: 'all', title: null, rows: data }]
  if (!isPlainObject(data)) return []
  // Every array-of-row-objects value is a candidate section
  const arrayEntries = Object.entries(data).filter(([, v]) => isRowArray(v))
  if (arrayEntries.length > 1) {
    return arrayEntries.map(([k, v]) => ({ key: k, title: labelFromKey(k), rows: v }))
  }
  if (arrayEntries.length === 1) {
    const [k, v] = arrayEntries[0]
    // A single well-known container key → flat grid (legacy behavior);
    // a single *named* section still gets its heading for clarity.
    return [{ key: k, title: CONTAINER_KEYS.includes(k) ? null : labelFromKey(k), rows: v }]
  }
  // No rows[] at this level — look one level down inside a container key
  // (e.g. { report: { timeSection: [...], expenseSection: [...] } }).
  if (depth < 2) {
    for (const k of CONTAINER_KEYS) {
      if (isPlainObject(data[k])) {
        const nested = toSections(data[k], depth + 1)
        if (nested.length) return nested
      }
    }
  }
  return []
}

/**
 * Status Report (legacy "Daily Report") — wraps DailyReport/GetDailyReport.
 * The CB endpoint takes a `DailyReportDefaults` query payload; we pass
 * through whatever the user types and render the result generically.
 */
// ⚠️ PascalCase on purpose — this endpoint takes a `DailyReportDefaults` payload
// through a DIFFERENT model binder than the camelCase specialized filter classes.
// Lowercasing these keys would bind to nothing and the report would silently
// return the server default window.
const FIELDS = [
  dateRangeField({ key: 'dates', label: 'Date Range', startKey: 'StartDate', endKey: 'EndDate', omitAll: true }),
]

const makeDefaults = () => ({
  StartDate: dayjs().startOf('day').format('YYYY-MM-DD'),
  EndDate: dayjs().endOf('day').format('YYYY-MM-DD'),
})

// CC-50148: `reportId` and `jobId` were always PASSED by ReportView
// (`ReportView.jsx` hands every specialized renderer reportId/name/summary/jobId)
// — this page just never declared them, which is why it had no queue control and
// no way to reopen a saved run. Both are optional: rendered without a reportId
// (any route that isn't the report view) the queue spec is withheld, so the split
// control never offers a background run that could not be created.
// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const StatusReport = ({ reportId, name = 'Status Report', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria (the split button's mid-run "Run report now" is also
  // disabled now; this gate is the structural guarantee). Spinner immediately
  // when reopening a saved result.
  const {
    running, error, ran, runKey, run: startRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  const [sections, setSections] = useState([])
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)
  // CC-50013 (G3): saved views. The hook carries the grid page state with it, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports say which day(s) the digest covers.
  const filtersSummary = useMemo(
    () => describeFilters(dateRangePart(filters.StartDate, filters.EndDate)),
    [filters]
  )

  const runReport = useCallback(() => startRun(async () => {
    const jid = jobRef.current
    jobRef.current = null // consumed — subsequent runs are fresh
    if (jid) {
      // Tray "View report" → reopen the exact saved snapshot + show its run time.
      const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
      return () => { setSavedAsOf(st?.createdAt || null); setSections(toSections(r?.data)) }
    }
    const data = await specializedReportService.daily.run(filters)
    // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
    return () => { setSavedAsOf(null); setSections(toSections(data)) }
  }, { criteria: { filtersSummary } }), [filters, startRun, filtersSummary])

  // Spec 131: deferred and cancellable, so StrictMode's simulated remount starts
  // ONE mount run, not two.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS).
  //
  // The queue spec is withheld unless a `reportId` is present, because a job
  // record requires one — so on any route that renders this page without one, the
  // split control correctly degrades to a plain Run button rather than offering a
  // background run that could never be created (CC-49833).
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={reportId ? {
        reportId,
        name,
        actionKey: 'daily',
        endpoint: (f) => ({ method: 'POST', path: '/reports/daily', body: f }),
      } : undefined}
      actions={(
        <ReportViewsMenu
          storageKey="report:status-daily"
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar}
      filtersSummary={filtersSummary}
      savedAsOf={savedAsOf}
    >
      {sections.length === 0 ? (
        <AutoReportGrid rows={[]} storageKey="report:status-daily" />
      ) : (
        sections.map((s) => (
          <div key={s.key} className={sections.length > 1 ? 'mb-6' : undefined}>
            {s.title && (
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">{s.title}</h2>
            )}
            {/* Saved page state is bound to the SINGLE-section shape only. When
                the digest splits into several sections each grid has its own
                storageKey, and one pageStateRef cannot represent all of them —
                the last grid to mount would overwrite the others' state and a
                restore would put section B's quick-filters on section A. In the
                multi-section case the saved view still carries filters and each
                grid's own persisted column layout; only the transient
                quick-filter state is out of scope. Widening this needs a
                keyed-by-section page-state shape (noted on CC-50013). */}
            <AutoReportGrid
              key={sections.length === 1 ? view.gridKey : undefined}
              /* One button per digest section would be wrong and ambiguous —
                 every one of them edits the same per-report alias map. Same
                 single-section seam the saved-view wiring above already uses. */
              suppressStripActions={sections.length > 1}
              rows={s.rows}
              storageKey={sections.length > 1 ? `report:status-daily:${s.key}` : 'report:status-daily'}
              pageStateRef={sections.length === 1 ? view.pageStateRef : undefined}
              initialPageState={sections.length === 1 ? view.initialPageState : undefined}
            />
          </div>
        ))
      )}
    </SpecializedReportShell>
  )
}

export default StatusReport
